import { NgClass } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import {
    FuseConfig,
    FuseConfigService,
    Scheme,
    Theme,
    Themes,
} from '@fuse/services/config';
import { UserService } from 'app/core/user/user.service';
import { User } from 'app/core/user/user.types';
import {
    DEFAULT_EMAIL_BANNER_COLOR_ID,
    EMAIL_BANNER_COLORS,
    EmailBannerColorOption,
    emailBannerGradient,
    findEmailBannerColor,
} from 'app/core/user/email-banner-colors';
import { AuthService } from 'app/core/auth/auth.service';
import { environment } from 'environments/environment';
import {
    isBusinessOperator,
    operatesAsIndependentSales,
    operatesAsTrader,
} from 'app/core/user/domain-roles';

import { Subject, takeUntil } from 'rxjs';
import {
    WAREHOUSE_STATIONS,
    WarehouseStationOption,
} from 'app/modules/admin/carts/warehouse-stations';

@Component({
    selector: 'settings',
    templateUrl: './settings.component.html',
    styles: [
        `
            settings {
                position: static;
                display: block;
                flex: none;
                width: auto;
            }

            @media (screen and min-width: 1280px) {
                empty-layout + settings .settings-cog {
                    right: 0 !important;
                }
            }
        `,
    ],
    encapsulation: ViewEncapsulation.None,
    imports: [
        MatIconModule,
        FuseDrawerComponent,
        MatButtonModule,
        NgClass,
        MatTooltipModule,
        MatSelectModule,
        MatFormFieldModule,
        MatCheckboxModule,
        FormsModule,
    ],
})
export class SettingsComponent implements OnInit, OnDestroy {
    config: FuseConfig;
    layout: string;
    scheme: 'dark' | 'light';
    theme: string;
    themes: Themes;
    user: User;
    defaultLandingPage: string = '';
    selectedLandingPage: string = '';
    isSaving: boolean = false;
    saveSuccess: boolean = false;
    selectedTheme: string = '';
    defaultTheme: string = '';
    selectedLayout: string = '';
    defaultLayout: string = '';
    isSavingTheme: boolean = false;
    isSavingLayout: boolean = false;
    saveThemeSuccess: boolean = false;
    saveLayoutSuccess: boolean = false;

    // Default Orders Workspace (modern layout only).
    // Picks which workspace the orders list rests in whenever any
    // other workspace panel closes — `''` = blank / idle.
    defaultOrdersWorkspace: string = '';
    selectedOrdersWorkspace: string = '';
    isSavingOrdersWorkspace: boolean = false;
    saveOrdersWorkspaceSuccess: boolean = false;

    ordersWorkspaceOptions = [
        { value: '', label: 'Blank / Idle (no fallback)' },
        { value: 'chat', label: 'Chat' },
        { value: 'email', label: 'Mailbox' },
    ];

    // Landing-workspace choices for Traders. Same persisted preference
    // as the admin "Default Orders Workspace", but worded for traders
    // and including Schedule (the resources workspace) since that's a
    // primary trader surface.
    traderWorkspaceOptions = [
        { value: '', label: 'Dash (Blank / Idle)' },
        { value: 'email', label: 'Mailbox' },
        { value: 'chat', label: 'Chat' },
        { value: 'resources', label: 'Schedule' },
    ];

    /**
     * Landing-workspace options for the Trader / Sales picker. Sales reps
     * don't have Resources access, so the "Schedule" (resources) option is
     * filtered out for them; Traders keep the full list.
     */
    get traderWorkspaceOptionsForRole(): { value: string; label: string }[] {
        if (
            operatesAsIndependentSales(this.user) &&
            !operatesAsTrader(this.user)
        ) {
            return this.traderWorkspaceOptions.filter((option) => option.value !== 'resources');
        }
        return this.traderWorkspaceOptions;
    }

    /** Business operators (Trader, Sales, Assistant) get landing-workspace prefs. */
    get showLandingWorkspacePicker(): boolean {
        return isBusinessOperator(this.user);
    }

    // "Send order updates to customers" self-service toggle.
    notifyCustomers: boolean = false;
    isSavingNotifyCustomers: boolean = false;
    saveNotifyCustomersSuccess: boolean = false;

    // Email header banner color (Trader / Independent Sales).
    emailBannerColors: EmailBannerColorOption[] = EMAIL_BANNER_COLORS;
    selectedEmailBannerColor: string = DEFAULT_EMAIL_BANNER_COLOR_ID;
    defaultEmailBannerColor: string = DEFAULT_EMAIL_BANNER_COLOR_ID;
    isSavingEmailBannerColor: boolean = false;
    saveEmailBannerColorSuccess: boolean = false;

    /**
     * The customer-notification toggle only changes behaviour for self-branded
     * reps — Traders and Independent Sales. Every other role always emails
     * customers regardless of the flag (mirrors BusinessIdentity on the
     * backend), so we only surface the control where it actually does something.
     */
    get showCustomerNotifyToggle(): boolean {
        return operatesAsTrader(this.user) || operatesAsIndependentSales(this.user);
    }

    /** Same audience as the customer-notify toggle — self-branded reps only. */
    get showEmailBannerColorPicker(): boolean {
        return this.showCustomerNotifyToggle;
    }

    get selectedEmailBannerPreview(): EmailBannerColorOption {
        return findEmailBannerColor(this.selectedEmailBannerColor);
    }

    readonly pressStations: WarehouseStationOption[] = WAREHOUSE_STATIONS.filter(
        (station) => station.group === 'press',
    );
    readonly loadStations: WarehouseStationOption[] = WAREHOUSE_STATIONS.filter(
        (station) => station.group === 'load',
    );
    selectedStationId: number | null = null;
    isSavingStation = false;
    saveStationSuccess = false;

    get canAssignStation(): boolean {
        const role = this.user?.role;
        return !!role
            && role !== 'Customer'
            && role !== 'Etsy_Customer'
            && role !== 'Shopify_Customer';
    }

    get userCompanyLogo(): string | null {
        const logo = (this.user as any)?.company_logo;
        return logo || null;
    }

    bannerGradient(option: EmailBannerColorOption): string {
        return emailBannerGradient(option);
    }

    // Available landing pages for admin users
    landingPageOptions = [
        { value: '/dashboard', label: 'Operations Dashboard' },
        { value: '/orders/list', label: 'Orders List' },
        { value: '/mailbox', label: 'Mailbox' },
        { value: '/shipping', label: 'Shipping' },
        { value: '/receiving', label: 'Receiving' },
        { value: '/purchasing', label: 'Purchasing' },
        { value: '/artwork', label: 'Artwork' },
        { value: '/resources', label: 'Resources' },
        { value: '/chat', label: 'Chat' },
        { value: '/image-gallery', label: 'Image Gallery' },
    ];

    // Available work pages for Production users (limited admin - each user gets ONE specialized page)
    // Default pages (Orders, Gallery, Chat, Mailbox) are always available
    productionLandingPageOptions = [
        { value: '/shipping', label: 'Shipping' },
        { value: '/delivery', label: 'Delivery' },
        { value: '/receiving', label: 'Receiving' },
        { value: '/purchasing', label: 'Purchasing' },
        { value: '/artwork', label: 'Artwork' },
        { value: '/resources', label: 'Resources' },
        { value: '/printer-view', label: 'Printer View' },
        { value: '/analytics', label: 'Analytics' },
    ];
    
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    private _httpClient = inject(HttpClient);
    private _authService = inject(AuthService);

    /**
     * Constructor
     */
    constructor(
        private _router: Router,
        private _fuseConfigService: FuseConfigService,
        private _userService: UserService
    ) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                // Store the config
                this.config = config;
            });

        // Subscribe to user changes to get default landing page, theme, and layout
        this._userService.user$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((user: User) => {
                this.user = user;
                // Get default landing page — check localStorage first (always up-to-date after save)
                // then fall back to database value from user data
                const savedPreference = localStorage.getItem('defaultLandingPage');
                const dbPreference = (user as any)?.default_landing_page || user?.defaultLandingPage;
                this.defaultLandingPage = savedPreference || dbPreference || '/orders/list';
                this.selectedLandingPage = this.defaultLandingPage;
                
                // Get theme color from user data (database only)
                const dbTheme = (user as any)?.theme_color || (user as any)?.themeColor;
                this.defaultTheme = dbTheme || this.config?.theme || '';
                this.selectedTheme = this.defaultTheme;
                
                // Get layout preference from user data (database only)
                const dbLayout = (user as any)?.layout_preference || (user as any)?.layoutPreference;
                this.defaultLayout = dbLayout || this.config?.layout || 'dense';
                this.selectedLayout = this.defaultLayout;

                // Default Orders Workspace — same hydration pattern as
                // defaultLandingPage above: prefer localStorage (last
                // save wins immediately) then fall back to user record.
                // Coerce null/undefined → '' so the dropdown always has
                // a concrete value bound.
                const savedWorkspace = localStorage.getItem('defaultOrdersWorkspace');
                const dbWorkspace = (user as any)?.default_orders_workspace
                    ?? (user as any)?.defaultOrdersWorkspace;
                this.defaultOrdersWorkspace =
                    savedWorkspace !== null ? savedWorkspace : (dbWorkspace ?? '');
                this.selectedOrdersWorkspace = this.defaultOrdersWorkspace;

                // Customer-notification toggle (self-branded reps only).
                const rawNotify = (user as any)?.notify_customers;
                this.notifyCustomers =
                    rawNotify === true || rawNotify === 1 || rawNotify === '1';

                const rawBanner = (user as any)?.email_banner_color;
                this.defaultEmailBannerColor = rawBanner || DEFAULT_EMAIL_BANNER_COLOR_ID;
                this.selectedEmailBannerColor = this.defaultEmailBannerColor;

                const rawStation = (user as any)?.station_id ?? (user as any)?.printer_cart_id;
                const stationId = Number(rawStation);
                this.selectedStationId =
                    Number.isFinite(stationId) && stationId > 0 ? stationId : null;
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Set the layout on the config
     *
     * @param layout
     */
    setLayout(layout: string): void {
        // Only allow dense and modern layouts
        if (layout !== 'dense' && layout !== 'modern') {
            return;
        }
        
        this.selectedLayout = layout;
        this.saveLayoutSuccess = false;
        
        // IMPORTANT: Update localStorage FIRST, before setting the config
        // Because _updateLayout() reads from localStorage and would override the config change
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                const userData = JSON.parse(userDataString);
                userData.layout_preference = layout;
                userData.layoutPreference = layout;
                localStorage.setItem('userData', JSON.stringify(userData));
            }
        } catch (e) {
            console.error('Error updating localStorage for layout:', e);
        }
        
        // Set the config (this triggers _updateLayout() in layout.component.ts)
        this._fuseConfigService.config = { layout };
        
        // Clear the 'layout' query param to allow layout changes (fire and forget)
        this._router.navigate([], {
            queryParams: { layout: null },
            queryParamsHandling: 'merge',
        });
        
        // Save to database
        this.saveLayoutPreference();
    }

    /**
     * Set the scheme on the config
     *
     * @param scheme
     */
    setScheme(scheme: Scheme): void {
        this._fuseConfigService.config = { scheme };
    }

    /**
     * Set the theme on the config
     *
     * @param theme
     */
    setTheme(theme: Theme): void {
        this.selectedTheme = theme;
        this.saveThemeSuccess = false;
        
        // Set the config
        this._fuseConfigService.config = { theme };
        // Save to database
        this.saveThemeColor();
    }

    /**
     * Handle landing page selection change - auto-saves to database
     *
     * @param page
     */
    onLandingPageChange(page: string): void {
        this.selectedLandingPage = page;
        this.saveSuccess = false;
        
        // Auto-save if the selection is different from the current default
        if (page !== this.defaultLandingPage) {
            this.saveDefaultLandingPage();
        }
    }

    /**
     * Save the default landing page preference
     */
    saveDefaultLandingPage(): void {
        // Allow Admin and Production users to set landing page
        if (!this.user || (this.user.role !== 'Admin' && this.user.role !== 'Production')) {
            return;
        }

        this.isSaving = true;
        this.saveSuccess = false;
        
        // Save to localStorage immediately (as fallback)
        localStorage.setItem('defaultLandingPage', this.selectedLandingPage);
        
        // Update user data
        const updatedUser = {
            ...this.user,
            defaultLandingPage: this.selectedLandingPage,
        };
        
        // Update user service
        this._userService.user = updatedUser;
        
        // Get user data from localStorage to access first_name and last_name
        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }
        
        // Use the update-profile endpoint to save default_landing_page
        // Backend requires first_name and last_name, so we get them from stored user data
        const updateData: any = {
            default_landing_page: this.selectedLandingPage,
        };
        
        // Add required fields if available
        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        // If first_name/last_name not in userData, try to parse from name field
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }
        
        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSaving = false;
                if (response.success || response.code === 200) {
                    console.log('Default landing page saved to database successfully', response);
                    console.log('Response data:', response.data);
                    // Update the displayed value
                    this.defaultLandingPage = this.selectedLandingPage;
                    // Update localStorage with the saved value
                    localStorage.setItem('defaultLandingPage', this.selectedLandingPage);
                    console.log('Saved to localStorage:', this.selectedLandingPage);
                    // Update userData in localStorage with the new default_landing_page
                    if (userData) {
                        userData.default_landing_page = this.selectedLandingPage;
                        userData.defaultLandingPage = this.selectedLandingPage;
                        localStorage.setItem('userData', JSON.stringify(userData));
                        console.log('Updated userData in localStorage:', userData);
                    }
                    // Also update the user service with the new value
                    if (response.data && response.data.default_landing_page) {
                        const updatedUser = {
                            ...this.user,
                            defaultLandingPage: response.data.default_landing_page,
                            default_landing_page: response.data.default_landing_page,
                        };
                        this._userService.user = updatedUser;
                        console.log('Updated user service:', updatedUser);
                    }
                    this.saveSuccess = true;
                    // Hide success message after 3 seconds
                    setTimeout(() => {
                        this.saveSuccess = false;
                    }, 3000);
                } else {
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSaving = false;
                console.error('Error saving default landing page to database:', error);
                // Still keep it in localStorage even if backend save fails
            }
        });
    }

    /**
     * Handle Default Orders Workspace selection change — auto-saves to
     * localStorage + the user's profile so the choice survives reloads
     * and follows the user across devices.
     */
    onOrdersWorkspaceChange(value: string): void {
        // mat-select can hand us `null` when the user picks the placeholder
        // option; normalize so the rest of the pipeline only deals with strings.
        const normalized = value ?? '';
        this.selectedOrdersWorkspace = normalized;
        this.saveOrdersWorkspaceSuccess = false;

        if (normalized !== this.defaultOrdersWorkspace) {
            this.saveDefaultOrdersWorkspace();
        }
    }

    /**
     * Save the Default Orders Workspace preference to localStorage and
     * the backend via /update-profile. Mirrors saveDefaultLandingPage()
     * — same Admin/Production gating, same userData hydration, same
     * graceful fallback when the backend call fails (localStorage still
     * holds the value so the runtime behavior on /orders/list works).
     */
    saveDefaultOrdersWorkspace(): void {
        // Available to Admin, Trader, and Sales. Production users have a
        // single specialized work page so the "rest in a workspace"
        // affordance doesn't apply to them.
        if (!this.user || (this.user.role !== 'Admin' && !isBusinessOperator(this.user))) {
            return;
        }

        this.isSavingOrdersWorkspace = true;
        this.saveOrdersWorkspaceSuccess = false;

        // Persist locally first so the /orders/list close-fallback
        // picks it up immediately even if the network is slow.
        localStorage.setItem('defaultOrdersWorkspace', this.selectedOrdersWorkspace);

        // Echo into the user service so other listeners see the new value.
        const updatedUser = {
            ...this.user,
            defaultOrdersWorkspace: this.selectedOrdersWorkspace,
        };
        this._userService.user = updatedUser as User;

        // Live signal to `/orders/list`. The orders list listens for
        // this on `document` and immediately re-applies the fallback
        // (no-op when a workspace is already mounted). Dispatched
        // BEFORE the HTTP round-trip so the panel mounts the instant
        // the user picks an option, even on a slow network.
        try {
            document.dispatchEvent(
                new CustomEvent('defaultOrdersWorkspaceChanged', {
                    detail: this.selectedOrdersWorkspace,
                }),
            );
        } catch (e) {
            console.warn('Failed to dispatch defaultOrdersWorkspaceChanged event:', e);
        }

        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        const updateData: any = {
            default_orders_workspace: this.selectedOrdersWorkspace,
        };

        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }

        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingOrdersWorkspace = false;
                if (response.success || response.code === 200) {
                    this.defaultOrdersWorkspace = this.selectedOrdersWorkspace;
                    localStorage.setItem('defaultOrdersWorkspace', this.selectedOrdersWorkspace);

                    if (userData) {
                        userData.default_orders_workspace = this.selectedOrdersWorkspace;
                        userData.defaultOrdersWorkspace = this.selectedOrdersWorkspace;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }

                    if (response.data && response.data.default_orders_workspace !== undefined) {
                        const refreshed = {
                            ...this.user,
                            defaultOrdersWorkspace: response.data.default_orders_workspace,
                            default_orders_workspace: response.data.default_orders_workspace,
                        };
                        this._userService.user = refreshed as User;
                    }

                    this.saveOrdersWorkspaceSuccess = true;
                    setTimeout(() => {
                        this.saveOrdersWorkspaceSuccess = false;
                    }, 3000);
                } else {
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSavingOrdersWorkspace = false;
                console.error('Error saving default orders workspace to database:', error);
                // localStorage already holds it — runtime fallback still works.
            },
        });
    }

    /**
     * Persist the "send order updates to customers" preference.
     * Only meaningful for self-branded reps (Trader / Independent Sales).
     */
    saveStation(id: number | null): void {
        if (!this.user || !this.canAssignStation || this.isSavingStation) {
            return;
        }
        if (this.selectedStationId === id) {
            return;
        }

        this.selectedStationId = id;
        this.isSavingStation = true;
        this.saveStationSuccess = false;

        const updatedUser = {
            ...this.user,
            station_id: id,
        };
        this._userService.user = updatedUser as User;

        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        const updateData: any = {
            station_id: id,
        };
        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }

        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingStation = false;
                if (response.success || response.code === 200) {
                    if (userData) {
                        userData.station_id = id;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                    this.saveStationSuccess = true;
                    setTimeout(() => {
                        this.saveStationSuccess = false;
                    }, 3000);
                }
            },
            error: (error) => {
                this.isSavingStation = false;
                console.error('Error saving station_id to database:', error);
            },
        });
    }

    saveNotifyCustomers(checked: boolean): void {
        if (!this.user || !this.showCustomerNotifyToggle) {
            return;
        }

        this.notifyCustomers = checked;
        this.isSavingNotifyCustomers = true;
        this.saveNotifyCustomersSuccess = false;

        // Echo into the user service + localStorage so other listeners and the
        // next drawer open reflect the new value immediately.
        const updatedUser = {
            ...this.user,
            notify_customers: checked,
        };
        this._userService.user = updatedUser as User;

        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        const updateData: any = {
            notify_customers: checked,
        };

        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }

        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingNotifyCustomers = false;
                if (response.success || response.code === 200) {
                    if (userData) {
                        userData.notify_customers = checked;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                    this.saveNotifyCustomersSuccess = true;
                    setTimeout(() => {
                        this.saveNotifyCustomersSuccess = false;
                    }, 3000);
                } else {
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSavingNotifyCustomers = false;
                // Roll back the optimistic toggle on failure.
                this.notifyCustomers = !checked;
                console.error('Error saving notify_customers to database:', error);
            },
        });
    }

    /**
     * Persist the email header banner color for self-branded reps.
     */
    selectEmailBannerColor(colorId: string): void {
        if (!this.user || !this.showEmailBannerColorPicker || this.isSavingEmailBannerColor) {
            return;
        }
        if (colorId === this.selectedEmailBannerColor) {
            return;
        }

        const previous = this.selectedEmailBannerColor;
        this.selectedEmailBannerColor = colorId;
        this.isSavingEmailBannerColor = true;
        this.saveEmailBannerColorSuccess = false;

        const updatedUser = {
            ...this.user,
            email_banner_color: colorId,
        };
        this._userService.user = updatedUser as User;

        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }

        const updateData: any = {
            email_banner_color: colorId,
        };

        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }

        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingEmailBannerColor = false;
                if (response.success || response.code === 200) {
                    this.defaultEmailBannerColor = colorId;
                    if (userData) {
                        userData.email_banner_color = colorId;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                    this.saveEmailBannerColorSuccess = true;
                    setTimeout(() => {
                        this.saveEmailBannerColorSuccess = false;
                    }, 3000);
                } else {
                    this.selectedEmailBannerColor = previous;
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSavingEmailBannerColor = false;
                this.selectedEmailBannerColor = previous;
                console.error('Error saving email_banner_color to database:', error);
            },
        });
    }

    /**
     * Save the theme color preference
     */
    saveThemeColor(): void {
        if (!this.user) {
            return;
        }

        this.isSavingTheme = true;
        this.saveThemeSuccess = false;
        
        // Get user data from localStorage to access first_name and last_name for the API call
        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }
        
        // Use the update-profile endpoint to save theme_color to database
        const updateData: any = {
            theme_color: this.selectedTheme,
        };
        
        // Add required fields if available
        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        // If first_name/last_name not in userData, try to parse from name field
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }
        
        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingTheme = false;
                if (response.success || response.code === 200) {
                    console.log('Theme color saved to database successfully', response);
                    this.defaultTheme = this.selectedTheme;
                    
                    // Update userData in localStorage with the new theme_color
                    if (userData) {
                        userData.theme_color = this.selectedTheme;
                        userData.themeColor = this.selectedTheme;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                    
                    // Update the user service
                    if (response.data && response.data.theme_color) {
                        const updatedUser = {
                            ...this.user,
                            themeColor: response.data.theme_color,
                            theme_color: response.data.theme_color,
                        };
                        this._userService.user = updatedUser;
                    }
                    
                    this.saveThemeSuccess = true;
                    setTimeout(() => {
                        this.saveThemeSuccess = false;
                    }, 3000);
                } else {
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSavingTheme = false;
                console.error('Error saving theme color to database:', error);
            }
        });
    }

    /**
     * Save the layout preference
     */
    saveLayoutPreference(): void {
        if (!this.user) {
            return;
        }

        this.isSavingLayout = true;
        this.saveLayoutSuccess = false;
        
        // Get user data from localStorage to access first_name and last_name for the API call
        let userData: any = {};
        try {
            const userDataString = localStorage.getItem('userData');
            if (userDataString) {
                userData = JSON.parse(userDataString);
            }
        } catch (e) {
            console.error('Error parsing userData from localStorage:', e);
        }
        
        // Use the update-profile endpoint to save layout_preference to database
        const updateData: any = {
            layout_preference: this.selectedLayout,
        };
        
        // Add required fields if available
        if (userData.first_name) {
            updateData.first_name = userData.first_name;
        }
        if (userData.last_name) {
            updateData.last_name = userData.last_name;
        }
        // If first_name/last_name not in userData, try to parse from name field
        if (!updateData.first_name && this.user.name) {
            const nameParts = this.user.name.split(' ');
            updateData.first_name = nameParts[0] || '';
            updateData.last_name = nameParts.slice(1).join(' ') || '';
        }
        
        this._httpClient.post(
            `${environment.apiUrl}/update-profile`,
            updateData,
            this._authService.getAuthHeaders()
        ).subscribe({
            next: (response: any) => {
                this.isSavingLayout = false;
                if (response.success || response.code === 200) {
                    console.log('Layout preference saved to database successfully', response);
                    this.defaultLayout = this.selectedLayout;
                    
                    // Update userData in localStorage with the new layout_preference
                    if (userData) {
                        userData.layout_preference = this.selectedLayout;
                        userData.layoutPreference = this.selectedLayout;
                        localStorage.setItem('userData', JSON.stringify(userData));
                    }
                    
                    // Update the user service
                    if (response.data && response.data.layout_preference) {
                        const updatedUser = {
                            ...this.user,
                            layoutPreference: response.data.layout_preference,
                            layout_preference: response.data.layout_preference,
                        };
                        this._userService.user = updatedUser;
                    }
                    
                    this.saveLayoutSuccess = true;
                    setTimeout(() => {
                        this.saveLayoutSuccess = false;
                    }, 3000);
                } else {
                    console.error('Unexpected response format:', response);
                }
            },
            error: (error) => {
                this.isSavingLayout = false;
                console.error('Error saving layout preference to database:', error);
            }
        });
    }
}
